import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import QRCode from 'react-qr-code';
import { 
  Link2, 
  Mail, 
  Phone, 
  MessageSquare, 
  Wifi, 
  MapPin, 
  User, 
  Calendar,
  Download,
  Copy,
  Check,
  QrCode,
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  MessageCircle,
  FileText,
  CreditCard
} from 'lucide-react';

const QR_TYPES = [
  { id: 'url', name: 'URL', icon: Link2, description: 'Website link' },
  { id: 'text', name: 'Text', icon: FileText, description: 'Plain text' },
  { id: 'email', name: 'Email', icon: Mail, description: 'Send email' },
  { id: 'phone', name: 'Phone', icon: Phone, description: 'Make a call' },
  { id: 'sms', name: 'SMS', icon: MessageSquare, description: 'Send SMS' },
  { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, description: 'WhatsApp message' },
  { id: 'wifi', name: 'WiFi', icon: Wifi, description: 'Connect to WiFi' },
  { id: 'vcard', name: 'vCard', icon: User, description: 'Contact card' },
  { id: 'location', name: 'Location', icon: MapPin, description: 'Google Maps' },
  { id: 'event', name: 'Event', icon: Calendar, description: 'Calendar event' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, description: 'Twitter profile' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, description: 'Instagram profile' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, description: 'LinkedIn profile' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, description: 'Facebook page' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, description: 'YouTube channel' },
  { id: 'businesscard', name: 'Business Card', icon: CreditCard, description: 'Digital card', isSpecial: true },
];

const QRGenerator = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedType, setSelectedType] = useState('url');
  const [qrData, setQrData] = useState('');
  const [qrGenerated, setQrGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrColor, setQrColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const qrRef = useRef(null);
  
  // Form data for different QR types
  const [formData, setFormData] = useState({
    // URL
    url: '',
    // Text
    text: '',
    // Email
    email: '',
    emailSubject: '',
    emailBody: '',
    // Phone
    phone: '',
    // SMS
    smsPhone: '',
    smsMessage: '',
    // WhatsApp
    whatsappPhone: '',
    whatsappMessage: '',
    // WiFi
    wifiSsid: '',
    wifiPassword: '',
    wifiEncryption: 'WPA',
    wifiHidden: false,
    // vCard
    vcardName: '',
    vcardOrg: '',
    vcardTitle: '',
    vcardPhone: '',
    vcardEmail: '',
    vcardWebsite: '',
    vcardAddress: '',
    // Location
    latitude: '',
    longitude: '',
    locationQuery: '',
    // Event
    eventTitle: '',
    eventLocation: '',
    eventStart: '',
    eventEnd: '',
    eventDescription: '',
    // Social
    twitterUsername: '',
    instagramUsername: '',
    linkedinUrl: '',
    facebookUrl: '',
    youtubeUrl: '',
  });

  const generateQRData = () => {
    let data = '';
    
    switch (selectedType) {
      case 'url':
        data = formData.url.startsWith('http') ? formData.url : `https://${formData.url}`;
        break;
      case 'text':
        data = formData.text;
        break;
      case 'email':
        data = `mailto:${formData.email}?subject=${encodeURIComponent(formData.emailSubject)}&body=${encodeURIComponent(formData.emailBody)}`;
        break;
      case 'phone':
        data = `tel:${formData.phone}`;
        break;
      case 'sms':
        data = `sms:${formData.smsPhone}${formData.smsMessage ? `?body=${encodeURIComponent(formData.smsMessage)}` : ''}`;
        break;
      case 'whatsapp':
        const waPhone = formData.whatsappPhone.replace(/[^0-9]/g, '');
        data = `https://wa.me/${waPhone}${formData.whatsappMessage ? `?text=${encodeURIComponent(formData.whatsappMessage)}` : ''}`;
        break;
      case 'wifi':
        const hidden = formData.wifiHidden ? 'H:true' : '';
        data = `WIFI:T:${formData.wifiEncryption};S:${formData.wifiSsid};P:${formData.wifiPassword};${hidden};`;
        break;
      case 'vcard':
        data = `BEGIN:VCARD
VERSION:3.0
N:${formData.vcardName}
FN:${formData.vcardName}
ORG:${formData.vcardOrg}
TITLE:${formData.vcardTitle}
TEL:${formData.vcardPhone}
EMAIL:${formData.vcardEmail}
URL:${formData.vcardWebsite}
ADR:${formData.vcardAddress}
END:VCARD`;
        break;
      case 'location':
        if (formData.latitude && formData.longitude) {
          data = `geo:${formData.latitude},${formData.longitude}`;
        } else if (formData.locationQuery) {
          data = `https://maps.google.com/?q=${encodeURIComponent(formData.locationQuery)}`;
        }
        break;
      case 'event':
        const formatDate = (dateStr) => dateStr ? dateStr.replace(/[-:]/g, '').replace('T', 'T') + '00' : '';
        data = `BEGIN:VEVENT
SUMMARY:${formData.eventTitle}
LOCATION:${formData.eventLocation}
DTSTART:${formatDate(formData.eventStart)}
DTEND:${formatDate(formData.eventEnd)}
DESCRIPTION:${formData.eventDescription}
END:VEVENT`;
        break;
      case 'twitter':
        data = `https://twitter.com/${formData.twitterUsername.replace('@', '')}`;
        break;
      case 'instagram':
        data = `https://instagram.com/${formData.instagramUsername.replace('@', '')}`;
        break;
      case 'linkedin':
        data = formData.linkedinUrl.startsWith('http') ? formData.linkedinUrl : `https://linkedin.com/in/${formData.linkedinUrl}`;
        break;
      case 'facebook':
        data = formData.facebookUrl.startsWith('http') ? formData.facebookUrl : `https://facebook.com/${formData.facebookUrl}`;
        break;
      case 'youtube':
        data = formData.youtubeUrl.startsWith('http') ? formData.youtubeUrl : `https://youtube.com/${formData.youtubeUrl}`;
        break;
      default:
        data = '';
    }
    
    setQrData(data);
    if (data) {
      setQrGenerated(true);
    }
  };

  const downloadQR = () => {
    if (!qrGenerated || !qrData) {
      toast({
        title: "Error",
        description: "Please generate a QR code first.",
        variant: "destructive"
      });
      return;
    }

    const svg = qrRef.current?.querySelector('svg');
    if (!svg) {
      toast({
        title: "Error",
        description: "QR code not found. Please try again.",
        variant: "destructive"
      });
      return;
    }

    // Clone and prepare SVG for download
    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute('width', '300');
    svgClone.setAttribute('height', '300');
    
    const svgData = new XMLSerializer().serializeToString(svgClone);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const qrImg = new Image();
    
    qrImg.onload = () => {
      // Canvas size: QR + padding + watermark area
      const padding = 20;
      const watermarkHeight = 28;
      canvas.width = 300 + (padding * 2);
      canvas.height = 300 + (padding * 2) + watermarkHeight;
      
      // Fill background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(qrImg, padding, padding, 300, 300);
      
      // Draw watermark text "Generated by pipercard.com" (subtle, centered)
      const watermarkY = padding + 300 + 20;
      ctx.fillStyle = '#9CA3AF'; // Light gray for subtle watermark
      ctx.font = '11px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Generated by pipercard.com', canvas.width / 2, watermarkY);
      
      // Download
      const link = document.createElement('a');
      link.download = 'qr-code-pipercard.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast({
        title: "Downloaded!",
        description: "QR code has been saved to your device."
      });
    };

    qrImg.onerror = (err) => {
      console.error('QR Image load error:', err);
      toast({
        title: "Download Failed",
        description: "Failed to generate image. Please try again.",
        variant: "destructive"
      });
    };
    
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    qrImg.src = URL.createObjectURL(svgBlob);
  };

  const copyQRData = () => {
    navigator.clipboard.writeText(qrData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderForm = () => {
    switch (selectedType) {
      case 'url':
        return (
          <div className="space-y-4">
            <div>
              <Label>Website URL</Label>
              <Input 
                placeholder="https://example.com"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'text':
        return (
          <div className="space-y-4">
            <div>
              <Label>Text Content</Label>
              <Textarea 
                placeholder="Enter your text here..."
                rows={4}
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'email':
        return (
          <div className="space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input 
                type="email"
                placeholder="email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Subject (Optional)</Label>
              <Input 
                placeholder="Email subject"
                value={formData.emailSubject}
                onChange={(e) => setFormData({...formData, emailSubject: e.target.value})}
              />
            </div>
            <div>
              <Label>Message (Optional)</Label>
              <Textarea 
                placeholder="Email body"
                rows={3}
                value={formData.emailBody}
                onChange={(e) => setFormData({...formData, emailBody: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'phone':
        return (
          <div className="space-y-4">
            <div>
              <Label>Phone Number</Label>
              <Input 
                type="tel"
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'sms':
        return (
          <div className="space-y-4">
            <div>
              <Label>Phone Number</Label>
              <Input 
                type="tel"
                placeholder="+91 98765 43210"
                value={formData.smsPhone}
                onChange={(e) => setFormData({...formData, smsPhone: e.target.value})}
              />
            </div>
            <div>
              <Label>Message (Optional)</Label>
              <Textarea 
                placeholder="Your SMS message"
                rows={3}
                value={formData.smsMessage}
                onChange={(e) => setFormData({...formData, smsMessage: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'whatsapp':
        return (
          <div className="space-y-4">
            <div>
              <Label>Phone Number (with country code)</Label>
              <Input 
                type="tel"
                placeholder="919876543210"
                value={formData.whatsappPhone}
                onChange={(e) => setFormData({...formData, whatsappPhone: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">Include country code without + (e.g., 919876543210)</p>
            </div>
            <div>
              <Label>Pre-filled Message (Optional)</Label>
              <Textarea 
                placeholder="Hi, I'm contacting you from..."
                rows={3}
                value={formData.whatsappMessage}
                onChange={(e) => setFormData({...formData, whatsappMessage: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'wifi':
        return (
          <div className="space-y-4">
            <div>
              <Label>Network Name (SSID)</Label>
              <Input 
                placeholder="My WiFi Network"
                value={formData.wifiSsid}
                onChange={(e) => setFormData({...formData, wifiSsid: e.target.value})}
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input 
                type="password"
                placeholder="WiFi password"
                value={formData.wifiPassword}
                onChange={(e) => setFormData({...formData, wifiPassword: e.target.value})}
              />
            </div>
            <div>
              <Label>Security Type</Label>
              <select 
                className="w-full border rounded-md p-2"
                value={formData.wifiEncryption}
                onChange={(e) => setFormData({...formData, wifiEncryption: e.target.value})}
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">None (Open)</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="hidden"
                checked={formData.wifiHidden}
                onChange={(e) => setFormData({...formData, wifiHidden: e.target.checked})}
              />
              <Label htmlFor="hidden" className="cursor-pointer">Hidden Network</Label>
            </div>
          </div>
        );
      
      case 'vcard':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input 
                  placeholder="John Doe"
                  value={formData.vcardName}
                  onChange={(e) => setFormData({...formData, vcardName: e.target.value})}
                />
              </div>
              <div>
                <Label>Organization</Label>
                <Input 
                  placeholder="Company Inc."
                  value={formData.vcardOrg}
                  onChange={(e) => setFormData({...formData, vcardOrg: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Job Title</Label>
                <Input 
                  placeholder="CEO"
                  value={formData.vcardTitle}
                  onChange={(e) => setFormData({...formData, vcardTitle: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={formData.vcardPhone}
                  onChange={(e) => setFormData({...formData, vcardPhone: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  placeholder="john@example.com"
                  value={formData.vcardEmail}
                  onChange={(e) => setFormData({...formData, vcardEmail: e.target.value})}
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input 
                  placeholder="https://example.com"
                  value={formData.vcardWebsite}
                  onChange={(e) => setFormData({...formData, vcardWebsite: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input 
                placeholder="123 Main St, City, Country"
                value={formData.vcardAddress}
                onChange={(e) => setFormData({...formData, vcardAddress: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'location':
        return (
          <div className="space-y-4">
            <div>
              <Label>Search Location</Label>
              <Input 
                placeholder="Eiffel Tower, Paris"
                value={formData.locationQuery}
                onChange={(e) => setFormData({...formData, locationQuery: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">Or enter coordinates below</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Latitude</Label>
                <Input 
                  placeholder="48.8584"
                  value={formData.latitude}
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input 
                  placeholder="2.2945"
                  value={formData.longitude}
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                />
              </div>
            </div>
          </div>
        );
      
      case 'event':
        return (
          <div className="space-y-4">
            <div>
              <Label>Event Title</Label>
              <Input 
                placeholder="Team Meeting"
                value={formData.eventTitle}
                onChange={(e) => setFormData({...formData, eventTitle: e.target.value})}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input 
                placeholder="Conference Room A"
                value={formData.eventLocation}
                onChange={(e) => setFormData({...formData, eventLocation: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date & Time</Label>
                <Input 
                  type="datetime-local"
                  value={formData.eventStart}
                  onChange={(e) => setFormData({...formData, eventStart: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date & Time</Label>
                <Input 
                  type="datetime-local"
                  value={formData.eventEnd}
                  onChange={(e) => setFormData({...formData, eventEnd: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea 
                placeholder="Event details..."
                rows={2}
                value={formData.eventDescription}
                onChange={(e) => setFormData({...formData, eventDescription: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'twitter':
        return (
          <div className="space-y-4">
            <div>
              <Label>Twitter/X Username</Label>
              <Input 
                placeholder="@username"
                value={formData.twitterUsername}
                onChange={(e) => setFormData({...formData, twitterUsername: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'instagram':
        return (
          <div className="space-y-4">
            <div>
              <Label>Instagram Username</Label>
              <Input 
                placeholder="@username"
                value={formData.instagramUsername}
                onChange={(e) => setFormData({...formData, instagramUsername: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'linkedin':
        return (
          <div className="space-y-4">
            <div>
              <Label>LinkedIn Profile URL or Username</Label>
              <Input 
                placeholder="https://linkedin.com/in/username"
                value={formData.linkedinUrl}
                onChange={(e) => setFormData({...formData, linkedinUrl: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'facebook':
        return (
          <div className="space-y-4">
            <div>
              <Label>Facebook Page URL or Username</Label>
              <Input 
                placeholder="https://facebook.com/pagename"
                value={formData.facebookUrl}
                onChange={(e) => setFormData({...formData, facebookUrl: e.target.value})}
              />
            </div>
          </div>
        );
      
      case 'youtube':
        return (
          <div className="space-y-4">
            <div>
              <Label>YouTube Channel URL</Label>
              <Input 
                placeholder="https://youtube.com/@channel"
                value={formData.youtubeUrl}
                onChange={(e) => setFormData({...formData, youtubeUrl: e.target.value})}
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Generate QR code SVG using a simple algorithm
  return (
    <div className="w-full">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Type Selection & Form */}
        <div className="space-y-6">
          {/* QR Type Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block">Select QR Code Type</Label>
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-4 gap-2">
              {QR_TYPES.map((type) => {
                const Icon = type.icon;
                const isBusinessCard = type.id === 'businesscard';
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      if (isBusinessCard) {
                        navigate('/create');
                        return;
                      }
                      setSelectedType(type.id);
                      setQrGenerated(false);
                    }}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      isBusinessCard
                        ? 'border-amber-500 bg-gradient-to-br from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
                        : selectedType === type.id
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-1" />
                    <span className="text-xs font-medium truncate w-full text-center">{type.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                {QR_TYPES.find(t => t.id === selectedType)?.icon && 
                  React.createElement(QR_TYPES.find(t => t.id === selectedType).icon, { className: "w-5 h-5" })
                }
                {QR_TYPES.find(t => t.id === selectedType)?.name} QR Code
              </h3>
              {renderForm()}
              
              {/* Color Options */}
              <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t">
                <div>
                  <Label className="text-sm">QR Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input 
                      type="color" 
                      value={qrColor}
                      onChange={(e) => setQrColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input 
                      value={qrColor}
                      onChange={(e) => setQrColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Background Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input 
                      type="color" 
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input 
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={generateQRData}
                className="w-full mt-6 bg-amber-600 hover:bg-amber-700"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Generate QR Code
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right: QR Preview & Download */}
        <div className="flex flex-col items-center">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div 
                ref={qrRef}
                className="aspect-square bg-white rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden qr-protected p-4"
                style={{ backgroundColor: bgColor }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  toast({
                    title: "Right-click Disabled",
                    description: "Please use the Download button to save the QR code."
                  });
                  return false;
                }}
              >
                {qrGenerated && qrData ? (
                  <QRCode
                    value={qrData}
                    size={280}
                    level="H"
                    fgColor={qrColor}
                    bgColor={bgColor}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                ) : (
                  <div className="text-center text-gray-400 p-8">
                    <QrCode className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">Your QR code will appear here</p>
                    <p className="text-xs mt-1">Fill in the form and click Generate</p>
                  </div>
                )}
              </div>

              {qrGenerated && qrData && (
                <div className="mt-6 space-y-3">
                  <div className="flex gap-2">
                    <Button 
                      onClick={downloadQR}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button 
                      onClick={copyQRData}
                      variant="outline"
                      className="flex-1"
                    >
                      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? 'Copied!' : 'Copy Data'}
                    </Button>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <Label className="text-xs text-gray-500">QR Data</Label>
                    <p className="text-xs font-mono break-all mt-1 text-gray-700">{qrData}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          <p className="text-xs text-gray-500 mt-4 text-center">
            Free QR codes. Scan with any QR reader app.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QRGenerator;
